import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardApi, scoresApi, schedule as scheduleApi } from '../utils/api';
import { useAuth } from '../utils/auth';
import FreelanceDashboard from './FreelanceDashboard';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
  Clock, Users, DoorOpen, TrendingUp, AlertCircle,
  CheckCircle, Calendar, CreditCard, RefreshCw, Trophy
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
         ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// ── Formatage temps ──────────────────────────────────────────
function fmtTime(sec) {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = sec % 60;
  return `${m}:${String(s).padStart(2,'0')}`;
}

// ── Carte salle live ─────────────────────────────────────────
function RoomCard({ room, onClick }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (room.current?.session_status !== 'running' || !room.current?.started_at) return;
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(room.current.started_at).getTime()) / 1000);
      setElapsed(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [room.current?.started_at, room.current?.is_running]);

  const STATUS = {
    playing:   { color:'var(--teal)',   label:'En jeu',      dot:'#00d2b4' },
    waiting:   { color:'var(--amber)',  label:'Démarre bientôt', dot:'#f5a623' },
    available: { color:'var(--text-3)', label:'Libre',        dot:'#3a3d50' },
  };
  const st = STATUS[room.status] || STATUS.available;
  const durationSec = (room.current?.duration_min || room.duration_min || 60) * 60;
  const remaining   = durationSec - elapsed;
  const pct         = room.status === 'playing' ? Math.min(elapsed / durationSec * 100, 100) : 0;

  // Calcul "libre jusqu'à"
  const nextSession = room.upcoming?.[0];
  const freeUntil = nextSession
    ? `Libre jusqu'à ${nextSession.session_start?.slice(0,5)}`
    : 'Aucune session ce soir';

  // Urgence : session dans moins de 30 min
  const isUrgent = room.status === 'available' && nextSession && (() => {
    const [h,m] = nextSession.session_start.split(':').map(Number);
    const now = new Date();
    const sessionMin = h*60+m;
    const nowMin = now.getHours()*60+now.getMinutes();
    return sessionMin - nowMin <= 30 && sessionMin - nowMin > 0;
  })();

  const borderColor = room.status === 'playing' ? '#00d2b4'
    : isUrgent ? '#f5a623'
    : room.status === 'waiting' ? '#f5a623'
    : '#2a2d3e';

  return (
    <div onClick={onClick} style={{
      background:'var(--bg-card)',
      border:`1px solid var(--border)`,
      borderRadius:14, padding:18, cursor:'pointer',
      borderLeft: `4px solid ${borderColor}`,
      transition:'all 0.15s',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,0.3)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
        <div style={{ fontWeight:700, fontSize:15, color:'var(--text-1)', flex:1, paddingRight:8 }}>{room.name}</div>
        <span style={{ fontSize:11, fontWeight:700, color:st.color,
          background:`${borderColor}18`, padding:'3px 9px', borderRadius:20, flexShrink:0 }}>
          {st.label}
        </span>
      </div>

      {/* En jeu */}
      {room.status === 'playing' && room.current && (
        <>
          <div style={{ marginBottom:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div>
                <div style={{ fontSize:13, color:'var(--text-2)', fontWeight:500 }}>
                  {room.current.customer_name}
                </div>
                <div style={{ fontSize:11, color:'var(--text-3)', marginTop:1 }}>
                  {room.current.player_count} joueur{room.current.player_count>1?'s':''}
                  {room.current.gm_first && ` · GM : ${room.current.gm_first} ${room.current.gm_last?.[0]||''}.`}
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ fontSize:22, fontWeight:800, fontFamily:'monospace', letterSpacing:'0.04em',
                  color: remaining < 300 ? 'var(--red)' : remaining < 600 ? 'var(--amber)' : 'var(--teal)' }}>
                  {fmtTime(Math.max(0, remaining))}
                </div>
                <div style={{ fontSize:10, color:'var(--text-3)', marginTop:1 }}>restant</div>
              </div>
            </div>
            <div style={{ background:'var(--border)', borderRadius:20, height:4, overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, height:'100%',
                background: pct > 85 ? 'var(--red)' : pct > 65 ? 'var(--amber)' : 'var(--teal)',
                borderRadius:20, transition:'width 1s linear' }}/>
            </div>
          </div>
          {/* Prochaine session */}
          {nextSession && (
            <div style={{ marginTop:8, paddingTop:8, borderTop:'1px solid var(--border)',
              fontSize:11, color:'var(--text-3)' }}>
              Suivante : <strong style={{color:'var(--text-2)'}}>{nextSession.session_start?.slice(0,5)}</strong>
              {' · '}{nextSession.player_count} joueur{nextSession.player_count>1?'s':''}
              {nextSession.gm_first && ` · ${nextSession.gm_first} ${nextSession.gm_last?.[0]||''}.`}
            </div>
          )}
        </>
      )}

      {/* En attente (session imminente) */}
      {room.status === 'waiting' && room.current && (
        <div>
          <div style={{ fontSize:13, color:'var(--text-2)', fontWeight:500, marginBottom:3 }}>
            {room.current.session_start?.slice(0,5)} · {room.current.customer_name}
          </div>
          <div style={{ fontSize:11, color:'var(--text-3)' }}>
            {room.current.player_count} joueur{room.current.player_count>1?'s':''}
            {room.current.gm_first && ` · GM : ${room.current.gm_first} ${room.current.gm_last?.[0]||''}.`}
          </div>
        </div>
      )}

      {/* Libre */}
      {room.status === 'available' && (
        <div>
          <div style={{ fontSize:12, color: isUrgent ? 'var(--amber)' : 'var(--text-3)', marginBottom: nextSession ? 4 : 0 }}>
            {freeUntil}
          </div>
          {nextSession && (
            <div style={{ fontSize:11, color:'var(--text-3)' }}>
              {nextSession.player_count} joueur{nextSession.player_count>1?'s':''}
              {nextSession.gm_first
                ? ` · GM : ${nextSession.gm_first} ${nextSession.gm_last?.[0]||''}.`
                : <span style={{color:'var(--red)'}}> · Pas de GM assigné</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── KPI card ─────────────────────────────────────────────────
function KpiCard({ label, value, sub, icon: Icon, color, trend }) {
  return (
    <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)',
      borderRadius:12, padding:'16px 18px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
        <div style={{ width:34, height:34, borderRadius:8, background:`${color}18`,
          display:'flex', alignItems:'center', justifyContent:'center' }}>
          <Icon size={16} color={color}/>
        </div>
        <span style={{ fontSize:12, color:'var(--text-3)' }}>{label}</span>
      </div>
      <div style={{ fontSize:26, fontWeight:800, color:'var(--text-1)', lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ── Score form ────────────────────────────────────────────────
function ScoreModal({ booking, room, onClose, onSaved }) {
  const [form, setForm] = useState({ escaped: true, min: '', sec: '00', hintsUsed: 0, notes: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const finishTimeSec = form.escaped
        ? parseInt(form.min || 0) * 60 + parseInt(form.sec || 0)
        : null;
      await scoresApi.add({
        bookingId: booking.id, roomId: room.id,
        escaped: form.escaped, finishTimeSec, hintsUsed: form.hintsUsed, notes: form.notes
      });
      onSaved();
      onClose();
    } catch(e) { alert(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{ maxWidth:420 }}>
        <h3 className="modal-title">
          <Trophy size={16}/> Enregistrer le score
        </h3>
        <p style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          {booking.customer_name} · {room.name}
        </p>

        <div style={{ display:'flex', gap:8, marginBottom:16 }}>
          {[true, false].map(v => (
            <button key={String(v)} onClick={() => setForm(p=>({...p, escaped:v}))}
              style={{ flex:1, padding:'10px', borderRadius:10, border:'none', cursor:'pointer',
                fontFamily:'inherit', fontWeight:600, fontSize:13,
                background: form.escaped===v ? (v?'rgba(0,210,180,0.15)':'rgba(255,77,109,0.15)') : 'var(--bg-elevated)',
                color: form.escaped===v ? (v?'var(--teal)':'var(--red)') : 'var(--text-3)',
                border: `1px solid ${form.escaped===v ? (v?'var(--teal)':'var(--red)') : 'var(--border)'}` }}>
              {v ? '✓ Sortis' : '✗ Non sortis'}
            </button>
          ))}
        </div>

        {form.escaped && (
          <div className="form-group">
            <label className="form-label">Temps de sortie</label>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="number" className="input" style={{ width:80 }} min={0} max={90}
                placeholder="mm" value={form.min} onChange={e=>setForm(p=>({...p,min:e.target.value}))}/>
              <span style={{ color:'var(--text-3)' }}>min</span>
              <input type="number" className="input" style={{ width:80 }} min={0} max={59}
                placeholder="ss" value={form.sec} onChange={e=>setForm(p=>({...p,sec:e.target.value}))}/>
              <span style={{ color:'var(--text-3)' }}>sec</span>
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Indices utilisés</label>
          <input type="number" className="input" min={0} value={form.hintsUsed}
            onChange={e=>setForm(p=>({...p,hintsUsed:parseInt(e.target.value)||0}))}/>
        </div>

        <div className="form-group">
          <label className="form-label">Notes (optionnel)</label>
          <textarea className="input" rows={2} value={form.notes}
            onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={{resize:'vertical'}}/>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────

// ── Bandeau pointage (tous rôles) ────────────────────────────
function ClockBanner() {
  const [isClockedIn, setIn] = useState(false);
  const [entry, setEntry]    = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const load = () => {
    scheduleApi.clockStatus()
      .then(r => { setIn(r.data.isClockedIn); setEntry(r.data.entry); })
      .catch(() => {});
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!isClockedIn || !entry?.clock_in) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now()-new Date(entry.clock_in).getTime())/1000));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [isClockedIn, entry?.clock_in]);

  const clockIn = async () => {
    try {
      const r = await scheduleApi.clockIn();
      setIn(true); setEntry(r.data.entry);
    } catch(e) { console.error(e); }
  };

  const clockOut = async () => {
    if (!entry) return;
    try { await scheduleApi.clockOut(entry.id); setIn(false); setEntry(null); setElapsed(0); }
    catch(e) { console.error(e); }
  };

  const hh = String(Math.floor(elapsed/3600)).padStart(2,'0');
  const mm = String(Math.floor((elapsed%3600)/60)).padStart(2,'0');
  const ss = String(elapsed%60).padStart(2,'0');

  return (
    <div style={{
      display:'flex', alignItems:'center', gap:12, padding:'10px 20px',
      background: isClockedIn ? 'rgba(0,210,180,0.06)' : 'var(--bg-elevated)',
      borderBottom: `1px solid ${isClockedIn ? 'rgba(0,210,180,0.2)' : 'var(--border)'}`,
      flexWrap:'wrap',
    }}>
      <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:0}}>
        <div style={{width:8,height:8,borderRadius:'50%',flexShrink:0,
          background:isClockedIn?'var(--teal)':'var(--text-3)',
          boxShadow:isClockedIn?'0 0 8px var(--teal)':'none'}}/>
        <span style={{fontSize:13,color:isClockedIn?'var(--teal)':'var(--text-3)',fontWeight:600}}>
          {isClockedIn ? 'En service' : 'Hors service'}
        </span>
        {isClockedIn && (
          <span style={{fontFamily:'monospace',fontSize:13,color:'var(--teal)',fontWeight:700,marginLeft:4}}>
            · {hh}:{mm}:{ss}
          </span>
        )}
      </div>
      {isClockedIn
        ? <button className="btn btn-danger btn-sm" style={{fontSize:12}} onClick={clockOut}>
            ⏹ Pointer la sortie
          </button>
        : <button className="btn btn-teal btn-sm" style={{fontSize:12}} onClick={clockIn}>
            ▶ Pointer l'entrée
          </button>
      }
    </div>
  );
}

export default function Dashboard() {
  const { user: authUser } = useAuth();
  if (authUser?.role === 'freelance') return <FreelanceDashboard />;

  const { can } = useAuth();
  const navigate = useNavigate();
  const [live, setLive]         = useState(null);
  const [stats, setStats]       = useState(null);
  const [scores, setScores]     = useState(null);
  const [period, setPeriod]     = useState('month');
  const [tab, setTab]           = useState('live');
  const [scoreModal, setScore]  = useState(null); // { booking, room }
  const [loading, setLoading]   = useState(true);
  const timerRef = useRef(null);

  const loadLive = useCallback(() => {
    dashboardApi.live().then(r => setLive(r.data)).catch(() => {});
  }, []);

  const loadStats = useCallback(() => {
    dashboardApi.stats(period).then(r => setStats(r.data)).catch(() => {});
    scoresApi.stats().then(r => setScores(r.data.stats)).catch(() => {});
  }, [period]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dashboardApi.live().then(r => setLive(r.data)),
      dashboardApi.stats(period).then(r => setStats(r.data)),
      scoresApi.stats().then(r => setScores(r.data.stats)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadStats(); }, [period]);

  // Refresh live toutes les 30s
  useEffect(() => {
    timerRef.current = setInterval(loadLive, 30000);
    return () => clearInterval(timerRef.current);
  }, [loadLive]);

  const COLORS = ['#7c5cfc','#00d2b4','#f5a623','#ff4d6d','#22d3a0','#a78bfa'];

  if (loading) return <div className="page-body"><div className="loading-spinner"/></div>;

  const kpis = stats?.kpis || {};
  const totalBookings = parseInt(kpis.total_bookings || 0);

  return (
    <>
      <ClockBanner/>
      <div className="page-header">
        <div>
          <h2>Tableau de bord</h2>
          <p>{format(new Date(), "EEEE d MMMM yyyy", { locale:fr })}</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button onClick={() => { loadLive(); loadStats(); }}
            style={{ background:'none', border:'1px solid var(--border)', borderRadius:8,
              padding:'6px 10px', cursor:'pointer', color:'var(--text-3)' }}>
            <RefreshCw size={14}/>
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* KPIs du jour */}
        {live?.stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:24 }}>
            <KpiCard label="Réservations aujourd'hui" value={live.stats.total_bookings || 0}
              icon={Calendar} color="var(--accent-light)"/>
            <KpiCard label="Joueurs attendus" value={live.stats.total_players || 0}
              icon={Users} color="var(--teal)"/>
            <KpiCard label="CA attendu" value={`${parseFloat(live.stats.total_revenue||0).toFixed(0)} €`}
              sub={`${parseFloat(live.stats.total_collected||0).toFixed(0)} € encaissé`}
              icon={TrendingUp} color="var(--accent-light)"/>
            <KpiCard label="Soldés" value={live.stats.paid_count || 0}
              sub={[
                live.stats.partial_count > 0 && `${live.stats.partial_count} acompte`,
                live.stats.unpaid_count > 0 && `${live.stats.unpaid_count} impayé`,
              ].filter(Boolean).join(' · ') || 'Tous soldés'}
              icon={CreditCard} color="var(--teal)"/>
            <KpiCard label="Impayés" value={`${parseFloat(live.stats.unpaid_amount||0).toFixed(0)} €`}
              icon={AlertCircle} color={parseFloat(live.stats.unpaid_amount||0) > 0 ? 'var(--red)' : 'var(--text-3)'}/>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', gap:8, marginBottom:20, borderBottom:'1px solid var(--border)', paddingBottom:0 }}>
          {[
            ['live',   'Temps réel',   DoorOpen],
            ['stats',  'Statistiques', TrendingUp],
            ['scores', 'Records',      Trophy],
          ].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 16px',
                borderRadius:'8px 8px 0 0', border:'none', cursor:'pointer',
                fontFamily:'inherit', fontSize:13, fontWeight: tab===id ? 700 : 400,
                background: tab===id ? 'var(--bg-card)' : 'none',
                color: tab===id ? 'var(--accent-light)' : 'var(--text-3)',
                borderBottom: tab===id ? '2px solid var(--accent)' : '2px solid transparent',
                marginBottom:-1 }}>
              <Icon size={14}/> {label}
            </button>
          ))}
        </div>

        {/* ── LIVE ── */}
        {tab === 'live' && (() => {
          const rooms = live?.rooms || [];
          if (!rooms.length) return <div className="empty-state"><p>Aucune salle configurée</p></div>;

          // Grouper par site
          const bySite = {};
          rooms.forEach(r => {
            const key = r.site_name || 'Sans site';
            if (!bySite[key]) bySite[key] = { color: r.site_color, rooms: [] };
            bySite[key].rooms.push(r);
          });
          const siteEntries = Object.entries(bySite);
          const multiSite = siteEntries.length > 1;

          return (
            <div style={{ display:'flex', flexDirection:'column', gap: multiSite ? 28 : 0 }}>
              {siteEntries.map(([siteName, { color, rooms: siteRooms }]) => (
                <div key={siteName}>
                  {multiSite && (
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                      <div style={{ width:10, height:10, borderRadius:'50%',
                        background: color || 'var(--accent)', flexShrink:0 }}/>
                      <span style={{ fontWeight:700, fontSize:14, color:'var(--text-1)' }}>{siteName}</span>
                      <div style={{ flex:1, height:1, background:'var(--border)', marginLeft:6 }}/>
                      <span style={{ fontSize:12, color:'var(--text-3)' }}>
                        {siteRooms.filter(r=>r.status==='playing').length} en jeu · {siteRooms.length} salles
                      </span>
                    </div>
                  )}
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:12 }}>
                    {siteRooms.map(room => (
                      <RoomCard key={room.id} room={room}
                        onClick={() => {
                          if (room.current) navigate(`/app/reservations/${room.current.id}`);
                        }}/>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── STATS ── */}
        {tab === 'stats' && stats && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              {[['week','7 jours'],['month','30 jours'],['year','1 an']].map(([v,l]) => (
                <button key={v} onClick={() => setPeriod(v)}
                  style={{ padding:'5px 14px', borderRadius:8, border:'none', cursor:'pointer',
                    fontFamily:'inherit', fontSize:13, fontWeight: period===v ? 600 : 400,
                    background: period===v ? 'rgba(124,92,252,0.15)' : 'var(--bg-elevated)',
                    color: period===v ? 'var(--accent-light)' : 'var(--text-3)' }}>
                  {l}
                </button>
              ))}
            </div>

            {/* KPIs période */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:24 }}>
              <KpiCard label="Réservations" value={kpis.total_bookings || 0} icon={Calendar} color="var(--accent-light)"/>
              <KpiCard label="CA total" value={`${parseFloat(kpis.total_revenue||0).toFixed(0)} €`} icon={TrendingUp} color="#22d3a0"/>
              <KpiCard label="Panier moyen" value={`${parseFloat(kpis.avg_basket||0).toFixed(0)} €`} icon={CreditCard} color="var(--teal)"/>
              <KpiCard label="Joueurs" value={kpis.total_players || 0} icon={Users} color="var(--amber)"/>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:16, marginBottom:16 }}>
              {/* CA par jour */}
              <div className="card">
                <div style={{ fontWeight:700, fontSize:14, marginBottom:14 }}>CA journalier</div>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.byDay} margin={{top:0,right:0,left:-20,bottom:0}}>
                    <XAxis dataKey="date" tick={{fontSize:10,fill:'var(--text-3)'}}
                      tickFormatter={d => d?.slice(5)}/>
                    <YAxis tick={{fontSize:10,fill:'var(--text-3)'}}/>
                    <Tooltip formatter={v=>[`${parseFloat(v).toFixed(0)} €`,'CA']}
                      contentStyle={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8}}/>
                    <Bar dataKey="revenue" fill="#7c5cfc" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Répartition paiements */}
              <div className="card">
                <div style={{ fontWeight:700, fontSize:14, marginBottom:14 }}>Paiements</div>
                {stats.byPayment?.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={140}>
                      <PieChart>
                        <Pie data={stats.byPayment} dataKey="total" nameKey="name"
                          cx="50%" cy="50%" outerRadius={55} innerRadius={30}>
                          {stats.byPayment.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]}/>
                          ))}
                        </Pie>
                        <Tooltip formatter={v=>[`${parseFloat(v).toFixed(0)} €`]}
                          contentStyle={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8}}/>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {stats.byPayment.map((p, i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                          <span style={{ color:COLORS[i%COLORS.length] }}>■ {p.name}</span>
                          <span style={{ color:'var(--text-2)' }}>{parseFloat(p.total).toFixed(0)} €</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : <div style={{ color:'var(--text-3)', fontSize:13 }}>Aucune donnée</div>}
              </div>
            </div>

            {/* Par salle */}
            <div className="card">
              <div style={{ fontWeight:700, fontSize:14, marginBottom:14 }}>Par salle</div>
              <div className="table-wrap">
                <table>
                  <thead><tr>
                    <th>Salle</th>
                    <th className="text-right">Réservations</th>
                    <th className="text-right">CA</th>
                    <th className="text-right">Joueurs</th>
                    <th className="text-right">Moy. joueurs</th>
                  </tr></thead>
                  <tbody>
                    {(stats.byRoom || []).map(r => (
                      <tr key={r.id}>
                        <td style={{ fontWeight:600 }}>{r.name}</td>
                        <td className="text-right">{r.bookings || 0}</td>
                        <td className="text-right">{parseFloat(r.revenue||0).toFixed(0)} €</td>
                        <td className="text-right">{r.players || 0}</td>
                        <td className="text-right">{parseFloat(r.avg_players||0).toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── SCORES ── */}
        {tab === 'scores' && (
          <div>
            {/* Stats par salle */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:12, marginBottom:20 }}>
              {(scores || []).map(s => (
                <div key={s.id} className="card" style={{ padding:'14px 16px' }}>
                  <div style={{ fontWeight:700, fontSize:14, marginBottom:10 }}>{s.name}</div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                    {[
                      ['Sessions', s.total_sessions || 0, 'var(--text-1)'],
                      ['Taux sortie', `${s.escape_rate || 0}%`, parseFloat(s.escape_rate||0)>50?'var(--teal)':'var(--amber)'],
                      ['Record', fmtTime(s.best_time), 'var(--accent-light)'],
                      ['Moy. indices', s.avg_hints || '—', 'var(--text-2)'],
                    ].map(([label, val, color]) => (
                      <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:2 }}>{label}</div>
                        <div style={{ fontSize:16, fontWeight:700, color }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Bouton enregistrer un score */}
            {can('staff') && live?.rooms && (
              <div className="card" style={{ marginBottom:16 }}>
                <div style={{ fontWeight:600, fontSize:14, marginBottom:12 }}>Enregistrer un score</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                  {live.rooms.filter(r => r.past[0]).map(room => (
                    <button key={room.id}
                      onClick={() => setScore({ booking: room.past[0], room })}
                      style={{ padding:'7px 14px', background:'rgba(124,92,252,0.1)',
                        border:'1px solid rgba(124,92,252,0.3)', borderRadius:8,
                        cursor:'pointer', color:'var(--accent-light)', fontSize:13,
                        fontFamily:'inherit' }}>
                      <Trophy size={12}/> {room.name} · {room.past[0].customer_name}
                    </button>
                  ))}
                  {!live?.rooms?.some(r => r.past[0]) && (
                    <div style={{ fontSize:13, color:'var(--text-3)' }}>
                      Aucune session terminée aujourd'hui à enregistrer
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {scoreModal && (
        <ScoreModal
          booking={scoreModal.booking}
          room={scoreModal.room}
          onClose={() => setScore(null)}
          onSaved={() => { loadLive(); loadStats(); scoresApi.stats().then(r=>setScores(r.data.stats)); }}
        />
      )}
    </>
  );
}
